import React from 'react';
import { Plus, Scan, Search, Camera } from 'lucide-react';

/**
 * "Add Items" card on the Create Sales Order page.
 * Owns the manual-entry form, barcode scan input, camera-scan trigger,
 * and product-search autocomplete. All callbacks/state are passed in
 * from the parent so this component stays pure presentational.
 */
export const AddItemsPanel = ({
  // Manual add
  showManualAdd,
  setShowManualAdd,
  manualItem,
  setManualItem,
  handleManualAdd,
  // Barcode form
  barcode,
  barcodeInputRef,
  handleBarcodeChange,
  handleBarcodePaste,
  handleBarcodeSubmit,
  onOpenCameraScanner,
  // Product search
  productSearch,
  handleProductSearchChange,
  productResults,
  searchingProducts,
  showProductDropdown,
  productDropdownRef,
  addProductToCart,
  // Pricing
  selectedScheme,
  priceForScheme,
}) => {
  return (
    <div className="bg-card rounded-lg border border-border p-6" data-testid="add-items-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Add Items</h3>
        <button
          onClick={() => setShowManualAdd(!showManualAdd)}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-border rounded-md hover:bg-muted"
          data-testid="toggle-manual-add"
        >
          <Plus className="h-4 w-4" /> Manual Item
        </button>
      </div>

      {showManualAdd && (
        <div className="mb-4 p-4 bg-muted/30 rounded-md">
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-2">
              <input
                type="text"
                placeholder="Description"
                value={manualItem.description}
                onChange={(e) => setManualItem({ ...manualItem, description: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-md"
                data-testid="manual-desc"
              />
            </div>
            <input
              type="number"
              min="1"
              placeholder="Qty"
              value={manualItem.quantity}
              onChange={(e) => setManualItem({ ...manualItem, quantity: parseInt(e.target.value) || 1 })}
              className="w-full px-3 py-2 border border-input rounded-md"
            />
            <div className="flex gap-2">
              <input
                type="number"
                step="0.01"
                placeholder="Price"
                value={manualItem.unit_price}
                onChange={(e) => setManualItem({ ...manualItem, unit_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-input rounded-md"
              />
              <button
                onClick={handleManualAdd}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 whitespace-nowrap"
                data-testid="add-manual-btn"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleBarcodeSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Scan className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Scan barcode — auto-adds to order..."
            ref={barcodeInputRef}
            value={barcode}
            onChange={handleBarcodeChange}
            onPaste={handleBarcodePaste}
            className="w-full pl-10 pr-4 py-3 border border-input rounded-md text-lg"
            data-testid="barcode-input"
            autoFocus
          />
        </div>
        <button
          type="submit"
          className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90"
          data-testid="barcode-add-btn"
        >
          Add
        </button>
        <button
          type="button"
          onClick={onOpenCameraScanner}
          className="px-4 py-3 border border-input rounded-md hover:bg-muted"
          data-testid="camera-scan-btn"
          title="Camera Scanner"
        >
          <Camera className="h-5 w-5" />
        </button>
      </form>

      {/* Product Search Autocomplete */}
      <div className="relative mt-3" ref={productDropdownRef}>
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by product name, SKU, or barcode..."
          value={productSearch}
          onChange={handleProductSearchChange}
          className="w-full pl-9 pr-4 py-2.5 border border-input rounded-md"
          data-testid="product-search-input"
        />
        {showProductDropdown && (
          <div
            className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg z-50 max-h-[28rem] overflow-y-auto"
            data-testid="product-dropdown"
          >
            {searchingProducts ? (
              <div className="p-3 text-sm text-muted-foreground text-center">Searching...</div>
            ) : productResults.length > 0 ? (
              <>
                {/* Sticky header showing how many matches are loaded — confirms the
                    list isn't truncated and reminds the user the list scrolls. */}
                <div className="sticky top-0 bg-muted/80 backdrop-blur px-3 py-1.5 text-xs text-muted-foreground border-b border-border z-10" data-testid="product-results-count">
                  {productResults.length} match{productResults.length === 1 ? '' : 'es'} — scroll to see all, or type more to narrow
                </div>
                {productResults.map((p) => (
                <button
                  key={p.variant_id}
                  onClick={() => addProductToCart(p)}
                  className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors border-b border-border last:border-b-0"
                  data-testid={`product-result-${p.variant_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{p.product_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        SKU: {p.sku} {p.barcode && `| BC: ${p.barcode}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">
                        ${priceForScheme(p, selectedScheme).toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground">Qty: {p.quantity_on_hand}</p>
                    </div>
                  </div>
                </button>
              ))}
              {productResults.length >= 500 && (
                <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-t border-amber-200 text-center" data-testid="product-results-cap-hint">
                  500+ matches — type more letters to narrow down
                </div>
              )}
              </>
            ) : (
              <div className="p-3 text-sm text-muted-foreground text-center">No products found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
